import { useEffect, useState } from 'react';
import { backdropClose } from '../../utils/backdropClose';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Info, Plus, Pencil, Trash2, Star, Building2, X } from 'lucide-react';
import { useApi } from '../../utils/useApi';
import toast from 'react-hot-toast';
import { validateGSTIN } from '../../utils/gstin';

interface Registration {
    id: string;
    gstin: string;
    legalName: string;
    stateCode: string;
    registeredAddress: string | null;
    invoicePrefix: string | null;
    status: string;
}
interface BranchMap {
    id: string;
    name: string;
    displayName: string | null;
    stateCode: string | null;
    gstRegistrationId: string | null;
}
interface RegData {
    defaultGstRegistrationId: string | null;
    /** The gym-level address on a Bill of Supply (and a Tax Invoice whose registration has none). */
    businessAddress?: string | null;
    registrations: Registration[];
    branches: BranchMap[];
}
interface RegForm {
    id?: string;
    gstin: string;
    legalName: string;
    registeredAddress: string;
    invoicePrefix: string;
    makeDefault: boolean;
}

const emptyForm: RegForm = { gstin: '', legalName: '', registeredAddress: '', invoicePrefix: '', makeDefault: false };

export default function GstinAndAddressSection({ gymId }: { gymId: string }) {
    const { fetchWithAuth } = useApi();
    const queryClient = useQueryClient();
    const key = ['gst-registrations', gymId];

    const { data, isLoading } = useQuery({
        queryKey: key,
        queryFn: async () => {
            const res = await fetchWithAuth(`/api/gst-registrations?gymId=${gymId}`);
            return res.data as RegData;
        },
        enabled: !!gymId,
    });

    const [form, setForm] = useState<RegForm | null>(null); // non-null = add/edit form open
    const [gstinError, setGstinError] = useState('');

    const invalidate = () => queryClient.invalidateQueries(key);

    const saveMutation = useMutation({
        mutationFn: async (f: RegForm) => {
            const body = {
                gymId,
                gstin: f.gstin.trim().toUpperCase(),
                legalName: f.legalName.trim(),
                registeredAddress: f.registeredAddress.trim() || null,
                invoicePrefix: f.invoicePrefix.trim() || null,
                makeDefault: f.makeDefault,
            };
            return f.id
                ? fetchWithAuth(`/api/gst-registrations/${f.id}`, { method: 'PUT', body: JSON.stringify(body) })
                : fetchWithAuth(`/api/gst-registrations`, { method: 'POST', body: JSON.stringify(body) });
        },
        onSuccess: () => { invalidate(); setForm(null); toast.success('Registration saved'); },
        onError: (err: any) => toast.error(err?.message || 'Failed to save registration'),
    });

    const deleteMutation = useMutation({
        mutationFn: async (id: string) => fetchWithAuth(`/api/gst-registrations/${id}`, { method: 'DELETE' }),
        onSuccess: () => { invalidate(); toast.success('Registration removed'); },
        onError: (err: any) => toast.error(err?.message || 'Failed to remove registration'),
    });

    const setDefaultMutation = useMutation({
        mutationFn: async (id: string) => fetchWithAuth(`/api/gst-registrations/${id}/set-default`, { method: 'POST' }),
        onSuccess: () => { invalidate(); toast.success('Default registration updated'); },
        onError: (err: any) => toast.error(err?.message || 'Failed to update default'),
    });

    const assignBranchMutation = useMutation({
        mutationFn: async (v: { branchId: string; registrationId: string | null }) =>
            fetchWithAuth(`/api/gst-registrations/assign-branch`, { method: 'POST', body: JSON.stringify(v) }),
        onSuccess: () => { invalidate(); toast.success('Branch updated'); },
        onError: (err: any) => toast.error(err?.message || 'Failed to update branch'),
    });

    const openAdd = () => { setForm({ ...emptyForm, makeDefault: (data?.registrations.length ?? 0) === 0 }); setGstinError(''); };
    const openEdit = (r: Registration) => {
        setForm({
            id: r.id, gstin: r.gstin, legalName: r.legalName,
            registeredAddress: r.registeredAddress || '',
            invoicePrefix: r.invoicePrefix || '',
            makeDefault: data?.defaultGstRegistrationId === r.id,
        });
        setGstinError('');
    };

    const submitForm = () => {
        if (!form) return;
        const gstin = form.gstin.trim().toUpperCase();
        if (!validateGSTIN(gstin) || !gstin) {
            setGstinError('Enter a valid 15-character GSTIN (e.g., 29AAAAA0000A1Z5).');
            return;
        }
        if (!form.legalName.trim()) { toast.error('Legal name is required'); return; }
        saveMutation.mutate(form);
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            </div>
        );
    }

    const registrations = data?.registrations ?? [];
    const branches = data?.branches ?? [];
    const defaultId = data?.defaultGstRegistrationId ?? null;

    return (
        <div className="p-4 sm:p-8 space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">GST Registrations</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Add each GSTIN (one per state). Link a branch to a registration to charge GST and issue a
                        Tax Invoice; leave it unlinked for a Bill of Supply (no GST).
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openAdd}
                    className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 flex-shrink-0"
                >
                    <Plus className="h-4 w-4" /> Add
                </button>
            </div>

            {/* ── Registrations list ── */}
            {registrations.length === 0 ? (
                <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex gap-3">
                    <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                        No GST registration yet. Until you add one and link your branches, all sales are issued as a
                        Bill of Supply with no GST.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {registrations.map(r => {
                        const isDefault = r.id === defaultId;
                        return (
                            <div key={r.id} className="border border-gray-200 rounded-xl p-4 space-y-2">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="font-bold text-gray-900 tracking-tight">{r.gstin}</p>
                                        <p className="text-sm text-gray-600">{r.legalName}</p>
                                    </div>
                                    {isDefault && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wide text-blue-600 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded-full">
                                            <Star className="h-3 w-3 fill-blue-600" /> Default
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                    <span>State {r.stateCode}</span>
                                    {r.invoicePrefix && <span>Prefix {r.invoicePrefix}</span>}
                                </div>
                                <div className="flex items-center gap-3 pt-1">
                                    <button onClick={() => openEdit(r)} className="text-xs font-semibold text-gray-600 hover:text-gray-900 flex items-center gap-1">
                                        <Pencil className="h-3.5 w-3.5" /> Edit
                                    </button>
                                    {!isDefault && (
                                        <button onClick={() => setDefaultMutation.mutate(r.id)} className="text-xs font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                            <Star className="h-3.5 w-3.5" /> Set default
                                        </button>
                                    )}
                                    <button onClick={() => deleteMutation.mutate(r.id)} className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1 ml-auto">
                                        <Trash2 className="h-3.5 w-3.5" /> Remove
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* ── Branch mapping ── */}
            {branches.length > 0 && (
                <div className="space-y-3">
                    <div>
                        <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-gray-400" /> Branch GST
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            Choose which registration each branch bills under. "Bill of Supply" means that branch does not charge GST.
                        </p>
                    </div>
                    <div className="overflow-x-auto border border-gray-200 rounded-xl">
                        <table className="w-full min-w-[560px] text-sm">
                            <thead className="bg-gray-50">
                                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    <th className="px-4 py-3">Branch</th>
                                    <th className="px-4 py-3">Registration</th>
                                    <th className="px-4 py-3 w-40">Document issued</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {branches.map(b => (
                                    <tr key={b.id} className="align-middle">
                                        <td className="px-4 py-3 font-medium text-gray-900">{b.displayName || b.name}</td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={b.gstRegistrationId || ''}
                                                onChange={(e) => assignBranchMutation.mutate({ branchId: b.id, registrationId: e.target.value || null })}
                                                className="w-full max-w-xs px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm"
                                            >
                                                <option value="">Bill of Supply (no GST)</option>
                                                {registrations.map(r => (
                                                    <option key={r.id} value={r.id}>{r.gstin} · {r.stateCode}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs font-bold uppercase tracking-wide ${b.gstRegistrationId ? 'text-teal-600' : 'text-gray-400'}`}>
                                                {b.gstRegistrationId ? 'Tax Invoice' : 'Bill of Supply'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="text-xs text-gray-400">Changes here apply instantly. Sales already made keep their original invoice unchanged.</p>
                </div>
            )}

            {/* ── Business address ── */}
            <BusinessAddress gymId={gymId} saved={data?.businessAddress ?? ''} onSaved={invalidate} />

            {/* ── Add / Edit registration modal ── */}
            {form && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" {...backdropClose(() => setForm(null))}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between">
                            <h4 className="text-lg font-bold text-gray-900">{form.id ? 'Edit registration' : 'Add GST registration'}</h4>
                            <button onClick={() => setForm(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"><X className="h-5 w-5" /></button>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">GSTIN</label>
                            <input
                                type="text"
                                value={form.gstin}
                                onChange={(e) => {
                                    const v = e.target.value.toUpperCase();
                                    setForm(f => f && { ...f, gstin: v });
                                    setGstinError(v && !validateGSTIN(v) ? 'Invalid GSTIN format.' : '');
                                }}
                                className={`w-full px-4 py-2.5 bg-gray-50 border rounded-xl focus:ring-2 focus:bg-white transition-all outline-none uppercase ${gstinError ? 'border-red-300 focus:ring-red-500' : 'border-gray-200 focus:ring-blue-500'}`}
                                placeholder="29AAAAA0000A1Z5"
                                maxLength={15}
                            />
                            {gstinError && <p className="text-sm text-red-600">{gstinError}</p>}
                            <p className="text-xs text-gray-500">State is derived automatically from the GSTIN.</p>
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">Legal name</label>
                            <input
                                type="text"
                                value={form.legalName}
                                onChange={(e) => setForm(f => f && { ...f, legalName: e.target.value })}
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none"
                                placeholder="Registered legal / trade name"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">Registered address</label>
                            <textarea
                                value={form.registeredAddress}
                                onChange={(e) => setForm(f => f && { ...f, registeredAddress: e.target.value })}
                                rows={2}
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none resize-none"
                                placeholder="Address as per registration..."
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">Invoice prefix</label>
                            <input
                                type="text"
                                value={form.invoicePrefix}
                                onChange={(e) => setForm(f => f && { ...f, invoicePrefix: e.target.value.toUpperCase() })}
                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none uppercase"
                                placeholder="Optional — defaults to gym code + state"
                                maxLength={20}
                            />
                            <p className="text-xs text-gray-500">Tax rate isn't set here — it comes from each plan / service / product.</p>
                        </div>

                        <label className="flex items-center gap-3 text-sm text-gray-700 cursor-pointer w-fit">
                            <input
                                type="checkbox"
                                checked={form.makeDefault}
                                onChange={(e) => setForm(f => f && { ...f, makeDefault: e.target.checked })}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            Use as the gym's default registration
                        </label>

                        <div className="flex justify-end gap-3 pt-2">
                            <button type="button" onClick={() => setForm(null)} className="px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">Cancel</button>
                            <button
                                type="button"
                                onClick={submitForm}
                                disabled={saveMutation.isLoading}
                                className="px-6 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2"
                            >
                                {saveMutation.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                                {form.id ? 'Save' : 'Add registration'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/**
 * The gym's business address: what a Bill of Supply prints under the gym's
 * name, and what a Tax Invoice falls back to when its registration has no
 * registered address of its own.
 */
function BusinessAddress({ gymId, saved, onSaved }: { gymId: string; saved: string; onSaved: () => void }) {
    const { fetchWithAuth } = useApi();
    const [value, setValue] = useState(saved);
    useEffect(() => { setValue(saved); }, [saved]);

    const save = useMutation({
        mutationFn: async () => fetchWithAuth(`/api/gst-registrations/business-address`, {
            method: 'PUT',
            body: JSON.stringify({ gymId, address: value }),
        }),
        onSuccess: () => { onSaved(); toast.success('Business address saved'); },
        onError: (err: any) => toast.error(err?.message || 'Failed to save the address'),
    });

    return (
        <div className="space-y-3">
            <div>
                <h3 className="text-base font-bold text-gray-900">
                    <label htmlFor="gym-business-address">Business address</label>
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                    Printed on a Bill of Supply under your gym's name. A Tax Invoice shows its registration's registered address
                    instead, and uses this one only if that is empty. A branch's own address is shown alongside.
                </p>
            </div>
            <textarea
                id="gym-business-address"
                rows={3}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all outline-none text-sm"
                placeholder="Building, street, city, PIN"
            />
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={() => save.mutate()}
                    disabled={save.isLoading || value.trim() === saved.trim()}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-all disabled:opacity-50"
                >
                    {save.isLoading ? 'Saving…' : 'Save address'}
                </button>
            </div>
        </div>
    );
}
